import { useEffect, useState } from 'react'
import { SESSION_EXPIRY_HOLD_STORAGE_KEY } from 'config'
import { runWhenTimeReached } from 'shared/utils'
import { getSessionExpiryHeldByOtherTabsUntil } from '../utils/sessionExpiryHold'

// The holds count only until the grace period ends, if it's given
const getHeldUntil = (gracePeriodEnd = Infinity) =>
  Math.min(getSessionExpiryHeldByOtherTabsUntil(), gracePeriodEnd)

// Updated when another tab changes its holds, and when the last hold runs out
export const useIsSessionExpiryHeldByAnotherTab = (gracePeriodEnd?: number) => {
  const [isHeld, setIsHeld] = useState(
    () => getHeldUntil(gracePeriodEnd) > Date.now()
  )

  useEffect(() => {
    let stopWaitingForHoldEnd: (() => void) | undefined

    const update = () => {
      stopWaitingForHoldEnd?.()
      const heldUntil = getHeldUntil(gracePeriodEnd)
      const isHeldNow = heldUntil > Date.now()
      setIsHeld(isHeldNow)
      if (isHeldNow) {
        stopWaitingForHoldEnd = runWhenTimeReached(heldUntil, update)
      }
    }

    const updateOnHoldsChange = ({ key }: StorageEvent) => {
      // A null key means the whole storage was cleared
      if (key === SESSION_EXPIRY_HOLD_STORAGE_KEY || key === null) update()
    }

    update()
    window.addEventListener('storage', updateOnHoldsChange)

    return () => {
      stopWaitingForHoldEnd?.()
      window.removeEventListener('storage', updateOnHoldsChange)
    }
  }, [gracePeriodEnd])

  return isHeld
}
